// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

interface CategoryData {
  group: string;
  example: string;
  time: boolean;
  cost: boolean;
}

export const HARMONIZED_CATEGORIES: Record<string, CategoryData> = {
  'Product design & development': {
    group: 'Engineering & setup',
    example: 'Mechanical/electrical design, prototyping',
    time: true,
    cost: true,
  },
  'Site planning & survey': {
    group: 'Engineering & setup',
    example: 'Floorplan work, cable routing, on-site assessments',
    time: true,
    cost: true,
  },
  'Hardware setup & staging': {
    group: 'Engineering & setup',
    example: 'Power setup, racking, ID labeling, barcoding',
    time: true,
    cost: true,
  },
  'Firmware & software configuration': {
    group: 'Engineering & setup',
    example: 'OS installs, firmware updates, remote provisioning',
    time: true,
    cost: true,
  },
  'Customization & engineering': {
    group: 'Engineering & setup',
    example: 'Electrical/mechanical mods, harness design, kit prep',
    time: true,
    cost: true,
  },
  'Integration & testing': {
    group: 'Engineering & setup',
    example: 'Bench tests, firmware QA, field commissioning',
    time: true,
    cost: true,
  },
  'Quality assurance & rework': {
    group: 'Engineering & setup',
    example: 'Field fixes, QA inspections, validation testing',
    time: true,
    cost: true,
  },
  'Deployment & installation': {
    group: 'Field activity',
    example: 'On-site setup, physical install, power and network connection',
    time: true,
    cost: true,
  },
  'Field service & repair': {
    group: 'Field activity',
    example: 'Troubleshooting, diagnostics, equipment repair',
    time: true,
    cost: true,
  },
  'Retrieval & decommission': {
    group: 'Field activity',
    example: 'Uninstall, asset return, end-of-service collection',
    time: true,
    cost: true,
  },
  Travel: {
    group: 'Field activity',
    example: 'Driving, flying, commuting between job sites',
    time: true,
    cost: true,
  },
  'Meals & entertainment': {
    group: 'Field activity',
    example: 'Per diems, meals while traveling, team/client dinners',
    time: false,
    cost: true,
  },
  'Parts & materials': {
    group: 'Materials & logistics',
    example: 'Replacement units, small parts, repair kits',
    time: false,
    cost: true,
  },
  'Tools & consumables': {
    group: 'Materials & logistics',
    example: 'Tape, PPE, handheld tools, zip ties',
    time: false,
    cost: true,
  },
  'Site rentals & equipment': {
    group: 'Materials & logistics',
    example: 'Scaffolding, lifts, rented trailers',
    time: false,
    cost: true,
  },
  'Shipping & freight': {
    group: 'Materials & logistics',
    example: 'Inbound or outbound logistics',
    time: false,
    cost: true,
  },
  'Permits & access fees': {
    group: 'Materials & logistics',
    example: 'Job site permits, union compliance, access cards',
    time: false,
    cost: true,
  },
  'Project planning & external coordination': {
    group: 'Coordination & engagement',
    example: 'Scoping work, managing resources, status tracking',
    time: true,
    cost: true,
  },
  'Team meetings & internal coordination': {
    group: 'Coordination & engagement',
    example: 'Standups, internal syncs, cross-functional planning',
    time: true,
    cost: true,
  },
  'Documentation & process development': {
    group: 'Coordination & engagement',
    example: 'Writing SOPs, documenting field lessons',
    time: true,
    cost: true,
  },
  'Customer training & onboarding': {
    group: 'Coordination & engagement',
    example: 'Training users, walking through documentation',
    time: true,
    cost: true,
  },
  'Support & issue resolution': {
    group: 'Coordination & engagement',
    example: 'Troubleshooting remotely, fielding post-deployment questions',
    time: true,
    cost: true,
  },
  'Field labor': {
    group: 'Labor & services',
    example: 'Hands-on work at a customer site by internal team',
    time: false,
    cost: true,
  },
  'Contracted labor': {
    group: 'Labor & services',
    example: 'Third-party techs, subcontractors',
    time: false,
    cost: true,
  },
  'Internal labor allocation': {
    group: 'Labor & services',
    example: 'General staff time across multiple projects',
    time: false,
    cost: true,
  },
  'Legal & regulatory services': {
    group: 'Professional services',
    example: 'Contract review, IP support, site-specific compliance advice',
    time: true,
    cost: true,
  },
  'Accounting & finance services': {
    group: 'Professional services',
    example: 'Bookkeeping, financial planning, tax advisory',
    time: true,
    cost: true,
  },
  'Strategic consulting': {
    group: 'Professional services',
    example: 'Field ops advisors, GTM consultants',
    time: true,
    cost: true,
  },
  'Training & certification': {
    group: 'Development & strategy',
    example: 'Online courses, safety training, certifications',
    time: true,
    cost: true,
  },
  'Research & innovation': {
    group: 'Development & strategy',
    example: 'Evaluating new tools, prototypes, experimental features',
    time: true,
    cost: true,
  },
  'Strategic planning': {
    group: 'Development & strategy',
    example: 'Roadmapping, long-term design discussions',
    time: true,
    cost: true,
  },
  Other: {
    group: 'Other',
    example: 'Unclassified or uncategorized time or cost',
    time: true,
    cost: true,
  },
};

export type CategoryType = 'time' | 'cost';

/**
 * Get the list of valid category values for a specific type
 */
export function getCategoryValues(type: CategoryType): string[] {
  return Object.entries(HARMONIZED_CATEGORIES)
    .filter(([_, data]) => data[type])
    .map(([category]) => category);
}

/**
 * Get the list of valid cost category values (legacy function)
 */
export function getCostCategoryValues(): string[] {
  return getCategoryValues('cost');
}

/**
 * Get the list of valid time category values
 */
export function getTimeCategoryValues(): string[] {
  return getCategoryValues('time');
}

/**
 * Check if a value is a valid category for the specified type
 */
export function isValidCategory(
  value: string | null | undefined,
  type: CategoryType
): boolean {
  return (
    !!value &&
    value in HARMONIZED_CATEGORIES &&
    HARMONIZED_CATEGORIES[value][type]
  );
}
